import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Delete, Lock, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import useAuth from '../store/useAuth'

const PIN_LENGTH = 6

const Login = () => {
  const [pin, setPin] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const login = useAuth((s) => s.login)

  const submit = useCallback(
    async (code) => {
      if (code.length !== PIN_LENGTH || loading) return
      setLoading(true)
      setError('')
      try {
        const res = await api.post('/api/auth/login', { pin: code })
        login(res.data.access_token, res.data.expires_in, res.data.role)
        toast.success(res.data.role === 'admin' ? 'Connexion administrateur réussie' : 'Connexion réussie')
        navigate('/', { replace: true })
      } catch (err) {
        const msg = err?.status === 401 ? 'Code PIN incorrect' : 'Erreur de connexion'
        setError(msg)
        setPin('')
      } finally {
        setLoading(false)
      }
    },
    [loading, login, navigate]
  )

  const appendDigit = (d) => {
    if (loading || pin.length >= PIN_LENGTH) return
    const next = pin + d
    setPin(next)
    if (next.length === PIN_LENGTH) {
      // auto-submit when the last digit is entered
      setTimeout(() => submit(next), 120)
    }
  }

  const backspace = () => {
    if (loading) return
    setPin((p) => p.slice(0, -1))
    setError('')
  }

  const clear = () => {
    if (loading) return
    setPin('')
    setError('')
  }

  // Keyboard support: digits, backspace, enter
  const onKeyDown = (e) => {
    if (loading) return
    if (e.key >= '0' && e.key <= '9') {
      appendDigit(e.key)
    } else if (e.key === 'Backspace') {
      backspace()
    } else if (e.key === 'Enter' && pin.length === PIN_LENGTH) {
      submit(pin)
    }
  }

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4"
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-600 mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TradeScope</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Saisissez votre code PIN à {PIN_LENGTH} chiffres
          </p>
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-3 mb-6">
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-colors ${
                i < pin.length
                  ? 'bg-primary-600 dark:bg-primary-400'
                  : 'bg-gray-300 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <p className="text-center text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
        )}

        {/* Numeric keypad */}
        <div className="grid grid-cols-3 gap-3">
          {keys.map((k) => (
            <button
              key={k}
              onClick={() => appendDigit(k)}
              disabled={loading}
              className="h-16 rounded-2xl text-2xl font-semibold bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95 transition disabled:opacity-50"
            >
              {k}
            </button>
          ))}
          <button
            onClick={clear}
            disabled={loading || !pin}
            className="h-16 rounded-2xl text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-95 transition disabled:opacity-30"
          >
            Effacer
          </button>
          <button
            onClick={() => appendDigit('0')}
            disabled={loading}
            className="h-16 rounded-2xl text-2xl font-semibold bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-95 transition disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={backspace}
            disabled={loading || !pin}
            className="h-16 rounded-2xl flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-95 transition disabled:opacity-30"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Delete className="w-6 h-6" />}
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-6">
          Vous pouvez aussi utiliser le clavier physique
        </p>
      </div>
    </div>
  )
}

export default Login
